import { Controller } from "@hotwired/stimulus"

// Signals > Data > Finance: connects the company's bank through Plaid Link.
// The button asks the server for a Link token, opens Plaid's window, and hands
// the public token back to the server, which keeps the bank login (encrypted)
// and reads the balance. A bank that signs in on its own site (OAuth, as Wells
// Fargo does) sends the browser to the oauth page, where `resume` is set and
// Link picks up with the same token, kept in sessionStorage for the trip.
const SCRIPT_URL = "https://cdn.plaid.com/link/v2/stable/link-initialize.js"
const TOKEN_KEY = "jane.finance.plaid_link_token"

export default class extends Controller {
    static targets = ["button", "status"]
    static values = { tokenUrl: String, connectUrl: String, resume: Boolean }

    connect() {
        if (this.resumeValue) this._resume()
    }

    async open() {
        this._busy(true, "Opening Plaid…")
        try {
            const response = await fetch(this.tokenUrlValue, { method: "POST", headers: this._headers() })
            const data = await response.json()
            if (!response.ok || !data.link_token) throw new Error(data.error || "Plaid could not be started.")
            this._remember(data.link_token)
            await this._load()
            this._handler(data.link_token).open()
        } catch (error) {
            this._busy(false, error.message)
        }
    }

    async _resume() {
        const token = this._recall()
        if (!token) return this._busy(false, "The bank sign-in expired. Start again from the Model tab.")
        this._busy(true, "Finishing the bank sign-in…")
        try {
            await this._load()
            this._handler(token, window.location.href).open()
        } catch (error) {
            this._busy(false, error.message)
        }
    }

    _handler(token, receivedRedirectUri) {
        const config = {
            token,
            onSuccess: (publicToken, metadata) => this._connect(publicToken, metadata),
            onExit: (error) => this._busy(false, error ? (error.display_message || error.error_message || "The bank was not connected.") : ""),
        }
        if (receivedRedirectUri) config.receivedRedirectUri = receivedRedirectUri
        return window.Plaid.create(config)
    }

    async _connect(publicToken, metadata) {
        this._forget()
        this._busy(true, "Connecting the bank…")
        try {
            const response = await fetch(this.connectUrlValue, {
                method: "POST",
                headers: this._headers(),
                body: JSON.stringify({ public_token: publicToken, institution_name: metadata?.institution?.name || "" }),
            })
            const data = await response.json()
            if (!response.ok) throw new Error(data.error || "The bank could not be connected.")
            window.location.assign(data.redirect)
        } catch (error) {
            this._busy(false, error.message)
        }
    }

    _load() {
        if (window.Plaid) return Promise.resolve()
        return new Promise((resolve, reject) => {
            const script = document.createElement("script")
            script.src = SCRIPT_URL
            script.onload = () => resolve()
            script.onerror = () => reject(new Error("Plaid's window could not be loaded."))
            document.head.appendChild(script)
        })
    }

    _headers() {
        const token = document.querySelector("meta[name='csrf-token']")?.content
        return { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token || "" }
    }

    _busy(busy, message) {
        if (this.hasButtonTarget) this.buttonTarget.disabled = busy
        if (this.hasStatusTarget) this.statusTarget.textContent = message || ""
    }

    _remember(token) { try { sessionStorage.setItem(TOKEN_KEY, token) } catch (_) { /* private mode: OAuth banks need a retry */ } }
    _recall() { try { return sessionStorage.getItem(TOKEN_KEY) } catch (_) { return null } }
    _forget() { try { sessionStorage.removeItem(TOKEN_KEY) } catch (_) { /* nothing kept */ } }
}
